import React, { useState, useRef, useEffect, useMemo } from 'react'
import { IoChevronDown, IoFlash } from 'react-icons/io5'
import {
  useLeague,
  LEAGUES_BY_COUNTRY,
  COUNTRY_FLAGS,
  TOP_LEAGUES,
  LIVE_TODAY_LEAGUE,
  isLiveTodayMode,
  type League,
} from '../context/LeagueContext'
import { useSidebar } from '../context/SidebarContext'
import { useLiveFixtureCounts, useTodayFixtureCount } from '../swr-config'

const COUNTRY_ORDER = ['England', 'Spain', 'Germany', 'Italy', 'France', 'Europe']

export function LeagueSwitcher() {
  const { currentLeague, setLeague, leagues } = useLeague()
  const { closeMobileMenu, isCollapsed } = useSidebar()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Get live fixture counts
  const leagueIds = useMemo(() => leagues.map((l) => l.api_league_id), [leagues])
  const { data: liveCounts } = useLiveFixtureCounts(leagueIds)

  // Get today's total fixture count for Live Today option
  const { data: todayCount } = useTodayFixtureCount()

  const isLiveToday = isLiveTodayMode(currentLeague)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleLeagueSelect = (league: League) => {
    setLeague(league)
    setIsOpen(false)
    closeMobileMenu()
  }

  const hasLive = (leagueId: number) => (liveCounts?.[leagueId] ?? 0) > 0

  // Collapsed sidebar state - show just the flag or lightning for Live Today
  if (isCollapsed) {
    return (
      <div className="league-switcher-collapsed">
        <button
          type="button"
          className={`league-switcher-collapsed-btn ${isLiveToday ? 'live-today' : ''}`}
          title={currentLeague.name}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isLiveToday ? <IoFlash size={20} /> : COUNTRY_FLAGS[currentLeague.country]}
          {!isLiveToday && hasLive(currentLeague.api_league_id) && <span className="live-indicator" />}
        </button>
      </div>
    )
  }

  return (
    <div className="league-switcher" ref={dropdownRef}>
      {/* Trigger button - shows current league */}
      <button
        type="button"
        className={`league-switcher-trigger ${isOpen ? 'open' : ''} ${isLiveToday ? 'live-today' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="league-switcher-flag">
          {isLiveToday ? <IoFlash size={18} /> : COUNTRY_FLAGS[currentLeague.country]}
        </span>
        <span className="league-switcher-name">{currentLeague.name}</span>
        {!isLiveToday && hasLive(currentLeague.api_league_id) && <span className="live-indicator" />}
        <IoChevronDown
          size={14}
          className={`league-switcher-chevron ${isOpen ? 'open' : ''}`}
        />
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="league-switcher-dropdown" role="listbox">
          {/* Live Today option */}
          <div className="league-switcher-live-today">
            <button
              type="button"
              className={`league-switcher-live-today-btn ${isLiveToday ? 'active' : ''}`}
              onClick={() => handleLeagueSelect(LIVE_TODAY_LEAGUE)}
              role="option"
              aria-selected={isLiveToday}
            >
              <IoFlash size={16} className="live-today-icon" />
              <span className="live-today-label">Live Today</span>
              {todayCount !== undefined && todayCount > 0 && (
                <span className="live-today-count">{todayCount}</span>
              )}
            </button>
          </div>

          {/* Quick access - Top 5 leagues */}
          <div className="league-switcher-quick">
            <div className="league-switcher-quick-label">Top Leagues</div>
            <div className="league-switcher-quick-chips">
              {TOP_LEAGUES.map((league) => {
                const isActive = currentLeague.api_league_id === league.api_league_id
                const isLive = hasLive(league.api_league_id)

                return (
                  <button
                    key={league.api_league_id}
                    type="button"
                    className={`league-chip ${isActive ? 'active' : ''}`}
                    onClick={() => handleLeagueSelect(league)}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span className="league-chip-flag">{COUNTRY_FLAGS[league.country]}</span>
                    <span className="league-chip-name">{league.shortName}</span>
                    {isLive && <span className="live-indicator" />}
                  </button>
                )
              })}
            </div>
          </div>

          {/* All leagues grouped by country */}
          <div className="league-switcher-groups">
            {COUNTRY_ORDER.map((country) => {
              const countryLeagues = LEAGUES_BY_COUNTRY[country]
              if (!countryLeagues || countryLeagues.length === 0) return null

              // Skip top leagues in grouped view if they're the only league for that country
              const nonTopLeagues = countryLeagues.filter(
                (l) => !TOP_LEAGUES.some((tl) => tl.api_league_id === l.api_league_id)
              )

              return (
                <div key={country} className="league-switcher-group">
                  <div className="league-switcher-group-header">
                    <span className="league-switcher-group-flag">{COUNTRY_FLAGS[country]}</span>
                    <span>{country}</span>
                  </div>
                  <div className="league-switcher-group-items">
                    {countryLeagues.map((league) => {
                      const isActive = currentLeague.api_league_id === league.api_league_id
                      const isLive = hasLive(league.api_league_id)
                      const isTopLeague = TOP_LEAGUES.some(
                        (tl) => tl.api_league_id === league.api_league_id
                      )

                      return (
                        <button
                          key={league.api_league_id}
                          type="button"
                          className={`league-switcher-item ${isActive ? 'active' : ''} ${isTopLeague ? 'top-league' : ''}`}
                          onClick={() => handleLeagueSelect(league)}
                          role="option"
                          aria-selected={isActive}
                        >
                          <span className="league-switcher-item-name">{league.shortName}</span>
                          {league.type === 'cup' && (
                            <span className="league-type-badge">Cup</span>
                          )}
                          {isLive && <span className="live-indicator" />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
